import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Inbox, Clock, CheckCircle2, Activity, Timer, ArrowRight, Star, Power, MessageSquareOff, Settings as SettingsIcon, X, AlertTriangle } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";

function formatDuration(seconds) {
    if (!seconds) return "—";
    if (seconds < 60) return `${Math.round(seconds)} сек`;
    if (seconds < 3600) return `${Math.round(seconds / 60)} мин`;
    if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} ч`;
    return `${(seconds / 86400).toFixed(1)} дн`;
}

const KPI_CARDS = [
    { key: "total", label: "Всего обращений", icon: Inbox, color: "text-slate-600 bg-slate-100" },
    { key: "awaiting", label: "Ожидают ответа", icon: Clock, color: "text-amber-700 bg-amber-50" },
    { key: "in_progress", label: "В работе", icon: Activity, color: "text-blue-700 bg-blue-50" },
    { key: "answered", label: "Отвечено", icon: CheckCircle2, color: "text-indigo-700 bg-indigo-50" },
];

export default function Dashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [toggleError, setToggleError] = useState("");

    useEffect(() => {
        api.get("/analytics/dashboard")
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
        api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    }, []);

    const avgRating = data?.kpis?.avg_rating || 0;
    const ratingCount = data?.kpis?.rating_count || 0;
    const accepting = settings?.status?.accepting;
    const acceptingReason = settings?.status?.reason;
    const localTime = settings?.status?.current_local_time;
    const canToggle = user?.role === "chairman" || user?.role === "head";
    // Only allow toggle when it's a manual on/off situation (not "off_hours" — that's schedule-driven)
    const canToggleNow = canToggle && (accepting || acceptingReason === "disabled");

    const handleToggle = async () => {
        setToggling(true);
        setToggleError("");
        try {
            const newValue = !settings.appeals_enabled;
            const { data: updated } = await api.patch("/settings", { appeals_enabled: newValue });
            setSettings(updated);
            setConfirmOpen(false);
        } catch (err) {
            setToggleError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setToggling(false);
        }
    };

    return (
        <div className="p-4 lg:p-10" data-testid="dashboard-page">
            <header className="mb-6 lg:mb-8 flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-2 hidden lg:block">
                        Кабинет
                    </div>
                    <h1 className="text-2xl lg:text-4xl font-heading font-medium tracking-tight text-slate-900">
                        Дашборд
                    </h1>
                    <p className="text-slate-500 mt-1 lg:mt-2 text-sm lg:text-base">
                        Обзор работы приёмной комиссии БГМК-2026
                    </p>
                </div>
                <Link
                    to="/appeals"
                    data-testid="dashboard-go-appeals"
                    className="inline-flex items-center gap-2 bg-brand text-white hover:bg-brand-hover transition-colors rounded-md px-4 py-2.5 font-medium text-sm"
                >
                    К обращениям <ArrowRight className="h-4 w-4" />
                </Link>
            </header>

            {/* Status banner — click to toggle (chairman/head only) */}
            {settings && (
                <div
                    data-testid="dashboard-status-banner"
                    className={`mb-5 lg:mb-6 rounded-lg border p-3 lg:p-4 flex items-center gap-3 lg:gap-4 ${
                        accepting
                            ? "bg-emerald-50 border-emerald-200"
                            : "bg-amber-50 border-amber-200"
                    }`}
                >
                    <div
                        className={`h-9 w-9 lg:h-10 lg:w-10 rounded-full flex items-center justify-center shrink-0 ${
                            accepting ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                        }`}
                    >
                        {accepting ? <Power className="h-4 w-4" /> : <MessageSquareOff className="h-4 w-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 text-sm lg:text-base">
                            {accepting
                                ? "Обращения принимаются"
                                : acceptingReason === "disabled"
                                ? "Приём обращений выключен"
                                : "Сейчас вне рабочих часов"}
                        </div>
                        <div className="text-xs lg:text-sm text-slate-600">
                            {accepting
                                ? "Бот пересылает новые обращения в кабинет."
                                : "Бот показывает абитуриентам уведомление, новые обращения не создаются."}
                            {localTime && (
                                <span className="ml-1.5 text-slate-500">· {localTime}</span>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {canToggleNow ? (
                            <button
                                onClick={() => setConfirmOpen(true)}
                                data-testid="dashboard-toggle-appeals"
                                className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                                    accepting
                                        ? "bg-white border border-amber-300 text-amber-700 hover:bg-amber-50"
                                        : "bg-emerald-600 text-white hover:bg-emerald-700 border border-emerald-600"
                                }`}
                            >
                                {accepting ? (
                                    <>
                                        <MessageSquareOff className="h-4 w-4" />
                                        <span className="hidden sm:inline">Приостановить</span>
                                        <span className="sm:hidden">Стоп</span>
                                    </>
                                ) : (
                                    <>
                                        <Power className="h-4 w-4" />
                                        <span className="hidden sm:inline">Возобновить</span>
                                        <span className="sm:hidden">Пуск</span>
                                    </>
                                )}
                            </button>
                        ) : null}
                        <Link
                            to="/settings"
                            data-testid="dashboard-status-settings-link"
                            className="p-2 rounded-md text-slate-500 hover:text-slate-800 hover:bg-white/60 transition-colors"
                            title="Настройки"
                        >
                            <SettingsIcon className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            )}

            {/* Confirm modal */}
            {confirmOpen && settings && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 animate-fade-in" data-testid="toggle-confirm-modal">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 lg:p-6">
                        <div className="flex items-start gap-3 mb-3">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${accepting ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                                {accepting ? <AlertTriangle className="h-5 w-5" /> : <Power className="h-5 w-5" />}
                            </div>
                            <div className="flex-1">
                                <h3 className="font-heading font-semibold text-slate-900 text-lg">
                                    {accepting ? "Приостановить приём обращений?" : "Возобновить приём обращений?"}
                                </h3>
                                <p className="text-sm text-slate-600 mt-1">
                                    {accepting
                                        ? "Бот сразу перестанет принимать новые обращения и будет показывать абитуриентам сообщение о временной приостановке. Уже открытые диалоги остаются доступными."
                                        : "Бот снова начнёт принимать обращения абитуриентов и пересылать их в кабинет."}
                                </p>
                            </div>
                            <button onClick={() => setConfirmOpen(false)} className="p-1 -m-1 rounded-md hover:bg-slate-100 text-slate-400" data-testid="toggle-confirm-close">
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        {accepting && settings.disabled_message && (
                            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 mb-4 text-sm text-slate-700 whitespace-pre-wrap max-h-40 overflow-auto" data-testid="disabled-message-preview">
                                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1.5">Сообщение абитуриентам:</div>
                                {settings.disabled_message}
                            </div>
                        )}

                        {toggleError && (
                            <div className="mb-3 text-sm bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2" data-testid="toggle-error">
                                {toggleError}
                            </div>
                        )}

                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={() => setConfirmOpen(false)}
                                disabled={toggling}
                                className="px-4 py-2 rounded-md text-sm font-medium text-slate-700 border border-slate-300 hover:bg-slate-50 disabled:opacity-60"
                            >
                                Отмена
                            </button>
                            <button
                                onClick={handleToggle}
                                disabled={toggling}
                                data-testid="toggle-confirm-submit"
                                className={`px-4 py-2 rounded-md text-sm font-medium text-white transition-colors disabled:opacity-60 ${accepting ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
                            >
                                {toggling ? "..." : accepting ? "Да, приостановить" : "Да, возобновить"}
                            </button>
                        </div>
                        {accepting && (
                            <p className="text-xs text-slate-500 mt-3">
                                Изменить текст сообщения можно в <Link to="/settings" className="underline hover:text-slate-700">Настройках</Link>.
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5 mb-5 lg:mb-8">
                {KPI_CARDS.map((card) => {
                    const value = data?.kpis?.[card.key] ?? 0;
                    return (
                        <div
                            key={card.key}
                            className="bg-white border border-slate-200 rounded-lg p-4 lg:p-5"
                            data-testid={`kpi-${card.key}`}
                        >
                            <div className="flex items-start justify-between mb-3 lg:mb-4">
                                <div className={`h-9 w-9 rounded-md flex items-center justify-center ${card.color}`}>
                                    <card.icon className="h-4 w-4" strokeWidth={1.5} />
                                </div>
                            </div>
                            <div className="text-2xl lg:text-3xl font-heading font-semibold text-slate-900 tracking-tight">
                                {loading ? "—" : value}
                            </div>
                            <div className="text-xs lg:text-sm text-slate-500 mt-1">{card.label}</div>
                        </div>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-5">
                <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-4 lg:p-6">
                    <div className="flex items-center justify-between mb-4 lg:mb-6">
                        <div>
                            <h3 className="font-heading font-medium text-slate-900">Динамика обращений</h3>
                            <p className="text-sm text-slate-500">За последние 14 дней</p>
                        </div>
                    </div>
                    <div className="h-56 lg:h-64" data-testid="dashboard-chart">
                        {data?.daily?.length > 0 && (
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data.daily}>
                                    <CartesianGrid stroke="#F1F5F9" vertical={false} />
                                    <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                    <Tooltip contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }} />
                                    <Line type="monotone" dataKey="count" stroke="#1E3A8A" strokeWidth={2} dot={{ r: 3, fill: "#1E3A8A" }} activeDot={{ r: 5 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                <div className="space-y-3 lg:space-y-5">
                    <div className="bg-white border border-slate-200 rounded-lg p-4 lg:p-6">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="h-9 w-9 rounded-md flex items-center justify-center bg-cyan-50 text-cyan-700">
                                <Timer className="h-4 w-4" strokeWidth={1.5} />
                            </div>
                            <h3 className="font-heading font-medium text-slate-900 text-sm lg:text-base">
                                Среднее время ответа
                            </h3>
                        </div>
                        <div
                            className="text-2xl lg:text-3xl font-heading font-semibold text-slate-900 tracking-tight"
                            data-testid="kpi-avg-response"
                        >
                            {loading ? "—" : formatDuration(data?.kpis?.avg_response_seconds)}
                        </div>
                        <p className="text-xs text-slate-500 mt-1.5">
                            от первого сообщения до ответа сотрудника
                        </p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-lg p-4 lg:p-6" data-testid="kpi-rating-card">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="h-9 w-9 rounded-md flex items-center justify-center bg-amber-50 text-amber-600">
                                <Star className="h-4 w-4" strokeWidth={1.5} />
                            </div>
                            <h3 className="font-heading font-medium text-slate-900 text-sm lg:text-base">
                                Оценка работы
                            </h3>
                        </div>
                        <div className="flex items-baseline gap-2">
                            <div className="text-2xl lg:text-3xl font-heading font-semibold text-slate-900 tracking-tight">
                                {loading || ratingCount === 0 ? "—" : avgRating.toFixed(1)}
                            </div>
                            {ratingCount > 0 && (
                                <div className="text-sm text-slate-500">/ 5</div>
                            )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1.5">
                            {ratingCount > 0
                                ? `на основе ${ratingCount} ${ratingCount === 1 ? "оценки" : "оценок"}`
                                : "пока нет оценок"}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
